import React, {useEffect} from 'react';
import {Alert} from '@mui/material';
import {GridValidRowModel} from '@mui/x-data-grid-pro';
import {Formik} from 'formik';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {useTableStyles} from '../../components/useTableStyles';
import useTitle from '../../hooks/useTitle';
import BulkAdminModal from '../../features/bulkAdmin/BulkAdminModal';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {
  BulkAdminPageHeader,
  BulkAdminPagePagination,
} from '../../features/bulkAdmin/BulkAdminPageHelpers';
import {
  initialBulkAdminSortable,
  initialSortingDirection,
  useBulkAdmin,
} from '../../features/bulkAdmin/useBulkAdmin';
import {initialBulkAdminValues} from '../../features/bulkAdmin/utils';
import {bulkAdminColumnsFactory} from '../../features/bulkAdmin/columns';
import BulkFiltersModal from '../../features/bulkAdmin/BulkFiltersModal';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {
  ActualBulkAdminRow,
  BulkAdminDetailPanelContent,
} from './BulkAdminDetailPanelContent';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';

/**
 * Bulk admin will be used only by PAD users, so it must not be visible to SPP users
 */
export default function BulkAdminPage() {
  const {
    bulkAccounts,
    loading,
    fetch,
    pageSize,
    startCursor,
    endCursor,
    hasNextPage,
    client,
    totalCount,
  } = useBulkAdmin();

  useEffect(() => {
    fetch({
      variables: {
        first: pageSize,
        orderBy: initialBulkAdminSortable,
        orderDirection: initialSortingDirection,
        after: null,
        searchTerm: null,
      },
    });
  }, []);

  const classes = useTableStyles();
  const {perms} = useDjangoContext();
  useTitle(`Bulk Admin`);
  if (!perms.includes(GlobalPermissionEnum.can_access_bulk_accounts)) {
    return <Alert severity="error">This component is not part of the SPP offering</Alert>;
  }
  return (
    <Formik initialValues={initialBulkAdminValues} onSubmit={() => {}}>
      {({setFieldValue, handleSubmit, values}) => {
        const {isBulkActionsModalOpen, activeFilter} = values;

        const cfColumns = bulkAdminColumnsFactory({
          fetch,
          pageSize,
          loading,
          client,
          values,
          setFieldValue,
        });

        return (
          <form onSubmit={handleSubmit}>
            <Panel
              title="Bulk Admin"
              style={{
                height: '100%',
              }}
              color={themeColors.lightAlert}
            >
              <BulkAdminPageHeader loading={loading} fetch={fetch} pageSize={pageSize} />
              <StickyDataGrid
                sx={{...classes.table, ...classes.hideColumnMenu}}
                hideFooterRowCount
                loading={loading}
                checkboxSelection
                onRowSelectionModelChange={(rowId) => {
                  setFieldValue('selectedIDs', rowId);
                }}
                columns={cfColumns}
                rows={bulkAccounts as readonly GridValidRowModel[]}
                getDetailPanelHeight={({row}) => 'auto'}
                getDetailPanelContent={({row}) => (
                  <BulkAdminDetailPanelContent row={row as ActualBulkAdminRow} />
                )}
                rowHeight={60}
                density="comfortable"
                data-test="bulk-admin-data-grid"
              />
              <BulkAdminPagePagination
                loading={loading}
                client={client}
                fetch={fetch}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                endCursor={endCursor}
                startCursor={startCursor}
                bulkAccountsLength={bulkAccounts.length}
                totalCount={totalCount}
              />
            </Panel>
            {isBulkActionsModalOpen && (
              <BulkAdminModal
                isOpen={isBulkActionsModalOpen}
                close={() => setFieldValue('isBulkActionsModalOpen', false)}
                bulkAccounts={bulkAccounts}
              />
            )}
            {activeFilter && (
              <BulkFiltersModal
                isOpen={!!activeFilter}
                close={() => setFieldValue('activeFilter', '')}
                fetch={fetch}
              />
            )}
          </form>
        );
      }}
    </Formik>
  );
}
